import FallbackClassImg from "assets/fallback.png"
import moment from "moment"
import styled, { css, keyframes } from "styled-components"
import { StarFullIcon } from "ui/icons/StarFullIcon"
import { defaultThemeColors, fonts, themeFontSize } from "ui/theme"
import { getAvatarUrl, getHighestDiscount, getLowestValidPrice } from "utils"
import { DiscountBadge } from "../discountBadge/DiscountBadge"
import { ClassCardSkeleton } from "./ClassCardSkeleton"

export const ClassCard = ({ classData, onClick, online_type }) => {
  if (!classData) {
    return <ClassCardSkeleton />
  }

  const discountList = classData?.discount_policy_for_private_class || []
  const highestDiscount = getHighestDiscount(classData)

  const isOnline = online_type === "online"

  const lowestPrice = isOnline
    ? classData?.price_per_online_class
    : getLowestValidPrice(
        classData?.type_of_class,
        classData?.price_per_group_class,
        classData?.price_per_private_class
      )

  return (
    <Container onClick={onClick}>
      <ImageContainer>
        {classData?.user?.gallery[0]?.name || classData?.profile_pic ? (
          <Img
            src={getAvatarUrl(
              classData?.user?.gallery[0]?.name || classData?.profile_pic
            )}
            alt={classData?.subject_sub_category || "Class image"}
          />
        ) : (
          <Img
            src={FallbackClassImg}
            alt='Class image'
          />
        )}
      </ImageContainer>

      <TextContainer>
        <TitleRow>
          <TitleText>{classData?.subject_sub_category}</TitleText>
          <RatingText>
            <StarFullIcon />
            <span>{Number(classData?.rate || 0).toFixed(1)}</span>
          </RatingText>
        </TitleRow>

        <PriceText>{`From $${lowestPrice} / person`}</PriceText>
        {classData?.next_available_class?.starts_at && (
          <AvailRow>
            <CalendarIcon />
            <span>
              <AvailBold>Available:</AvailBold>{" "}
              {moment(classData.next_available_class.starts_at).format(
                "MMM D, h:mm a"
              )}
            </span>
          </AvailRow>
        )}
        <SeeMoreText>See more</SeeMoreText>
        {highestDiscount && <DiscountBadge discount={highestDiscount} />}
      </TextContainer>
    </Container>
  )
}

const fade = keyframes`
    
`

const fadeEffect = css`
  animation: ${fade} 2s infinite ease-in-out;
  background: ${defaultThemeColors.inputBgNeutral};
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: fit-content;
  cursor: pointer;
`

const ImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3.6;
  border-radius: 16px;
  overflow: hidden;
`

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.4s ease;
  background: #0000000d;
  ${({ animate }) => (animate ? fadeEffect : null)}

  ${Container}:hover & {
    transform: scale(1.05);
  }
`

const TextContainer = styled.div`
  padding: 14px 2px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  line-height: 1.5;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const TitleText = styled.p`
  font-size: 18px;
  font-weight: 500;
  color: ${defaultThemeColors.txt};
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const RatingText = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 400;
  color: ${defaultThemeColors.basicBlack};

  & > svg {
    width: 12px;
    height: 12px;
    color: ${defaultThemeColors.orange}; 
  }
`

const PriceText = styled.p`
  font-size: 14px;
  font-weight: 300;
  color: #6b6b6b;
  line-height: 1.5;
`

const CalendarIcon = () => (
  <svg
    width='13'
    height='20'
    viewBox='0 0 13 13'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'>
    <rect
      x='1'
      y='2'
      width='11'
      height='10'
      rx='2'
      stroke='#1d1d1f'
      strokeWidth='1.2'
    />
    <path
      d='M1 5H12'
      stroke='#1d1d1f'
      strokeWidth='1.2'
      strokeLinecap='round'
    />
    <path
      d='M4 1V3'
      stroke='#1d1d1f'
      strokeWidth='1.2'
      strokeLinecap='round'
    />
    <path
      d='M9 1V3'
      stroke='#1d1d1f'
      strokeWidth='1.2'
      strokeLinecap='round'
    />
  </svg>
)

const AvailRow = styled.div`
  display: flex;
  gap: 5px;
  font-family: ${fonts.families.primary};
  font-size: ${themeFontSize("small")};
  font-weight: 400;
  color: #6b6b6b;
  margin: 2px 0;
`

const AvailBold = styled.span`
  font-family: ${fonts.families.primary};
  font-weight: 500;
  color: #1d1d1f;
`

const SeeMoreText = styled.button`
  all: unset;
  display: none;
  font-size: ${themeFontSize("small")};
  font-weight: 600;
  color: ${defaultThemeColors.basicBlack};
  margin-top: 2px;
  cursor: pointer;

  @media (min-width: 768px) {
    display: block;
  }

  &:hover {
    color: ${defaultThemeColors.blue};
  }
`
